import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';

export type ThemeName = 'light' | 'dark';

export interface Theme {
  name: ThemeName;
  label: string;
  path: string;
  metaThemeColor: string; // Add this
}

export const themes: Theme[] = [
  { name: 'light', label: 'Light', path: '/themes/light.css', metaThemeColor: '#cea475' },
  { name: 'dark', label: 'Dark', path: '/themes/dark.css', metaThemeColor: '#353432' },
];

const THEME_STORAGE_KEY = 'ancana-theme';
const THEME_LINK_ID = 'theme-stylesheet';

interface ThemeContextType {
  currentTheme: ThemeName;
  setTheme: (theme: ThemeName) => Promise<void>;
  loading: boolean;
  themes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Helper function to update meta theme-color
const updateMetaThemeColor = (color: string) => {
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', color);
  }
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>('light');
  const [loading, setLoading] = useState<boolean>(false);

  const loadThemeCSS = (themePath: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      let themeLink = document.getElementById(THEME_LINK_ID) as HTMLLinkElement;

      if (!themeLink) {
        themeLink = document.createElement('link');
        themeLink.id = THEME_LINK_ID;
        themeLink.rel = 'stylesheet';
        document.head.appendChild(themeLink);
      }

      const newLink = document.createElement('link');
      newLink.rel = 'stylesheet';
      newLink.href = themePath;

      newLink.onload = () => {
        if (themeLink && themeLink.parentNode) {
          themeLink.parentNode.replaceChild(newLink, themeLink);
          newLink.id = THEME_LINK_ID;
        }
        resolve();
      };

      newLink.onerror = () => {
        reject(new Error(`Failed to load theme: ${themePath}`));
      };

      document.head.appendChild(newLink);

      if (themeLink.href === newLink.href) {
        document.head.removeChild(newLink);
        resolve();
      }
    });
  };

  // Load saved theme on mount
  useEffect(() => {
    import('@ant-design/icons').then(({ setTwoToneColor }) => {
      const savedTheme = (localStorage.getItem(THEME_STORAGE_KEY) as ThemeName) || 'light';
      const theme = themes.find(t => t.name === savedTheme);

      setCurrentTheme(savedTheme);

      // Configure TwoTone icon colors based on theme
      if (savedTheme === 'dark') {
        setTwoToneColor(['#c49662', '#333333']);
      } else {
        setTwoToneColor('#c49662');
      }

      // Update meta theme-color
      if (theme) {
        updateMetaThemeColor(theme.metaThemeColor);
      }

      // Apply the saved theme immediately if not default
      if (savedTheme !== 'light') {
        if (theme) {
          loadThemeCSS(theme.path).catch(console.error);
        }
      }

      // Set body class
      document.body.className = savedTheme === 'dark' ? 'theme-dark' : 'theme-light';
    });
  }, []);

  const setTheme = useCallback(async (themeName: ThemeName) => {
    const theme = themes.find(t => t.name === themeName);
    if (!theme) return;

    setLoading(true);

    try {
      await loadThemeCSS(theme.path);

      // Configure TwoTone icon colors based on theme
      const { setTwoToneColor } = await import('@ant-design/icons');
      if (themeName === 'dark') {
        setTwoToneColor(['#c49662', '#333333']);
      } else {
        setTwoToneColor('#c49662');
      }

      setCurrentTheme(themeName);
      localStorage.setItem(THEME_STORAGE_KEY, themeName);

      document.body.className = themeName === 'dark' ? 'theme-dark' : 'theme-light';

      // Update meta theme-color
      updateMetaThemeColor(theme.metaThemeColor);

    } catch (error) {
      console.error('Failed to switch theme:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, loading, themes }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
